import * as React from 'react';
import { SubtractUniversal } from '@duxton-web/icons';
import { Alert, Button, Tag, Tooltip, Typography } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { capitalize } from 'lodash';
import { Line } from 'react-chartjs-2';

import { ABIcon } from '../../../common/Icons/ABIcon';
import { AdaptiveIcon } from '../../../common/Icons/AdaptiveIcon';
import { ADAPTIVE } from '../../../constants';
import Service from '../../../lib/App';
import { SuperExperiment, Variable } from '../../../lib/PlannerDTOs';
import { AdaptiveTrialsCount } from '../AdaptiveDetailSubPage/AdaptiveDetail';
import {
  calculateDaysDifference,
  displayMonitoringGraph,
  extractCompletionProgress,
  options,
  transformDate,
  transformProgress,
  truncate,
} from '../helper/utils';
import {
  formatUTCStringToDateTimeString,
  getTimezoneDisplay,
} from '@/utils/dateTime';

const { Text } = Typography;

interface MonitoringGraphProps {
  experimentName: string;
}

interface MonitoringGraphState {
  isLoading: boolean;
  errorMessage: string;
  labels: any[];
  datasets: any[];
}

export class MonitoringGraph extends React.Component<
  MonitoringGraphProps,
  MonitoringGraphState
> {
  state = {
    isLoading: false,
    errorMessage: '',
    labels: [],
    datasets: [],
  };

  handleClick = async () => {
    const { experimentName } = this.props;
    try {
      const { labels, datasets } = await displayMonitoringGraph(experimentName);
      if (labels.length === 0 || datasets.length === 0) {
        return this.setState({
          errorMessage: 'No Data Available. Please try again later.',
        });
      }
      this.setState({ labels, datasets });
    } catch (err) {
      console.error(err);
    }
  };

  handleAlertClose = () => {
    this.setState({ errorMessage: '' });
  };

  render() {
    const { labels, datasets, errorMessage } = this.state;
    return (
      <>
        {labels.length > 0 && datasets.length > 0 ? (
          <Line
            legend={{ display: true }}
            options={options}
            data={{ labels, datasets }}
            height={90}
          />
        ) : (
          <>
            {errorMessage ? (
              <Alert
                showIcon
                type="error"
                message={errorMessage}
                closable
                onClose={this.handleAlertClose}
              />
            ) : (
              <Button onClick={this.handleClick}>Show Graph</Button>
            )}
          </>
        )}
      </>
    );
  }
}

type HealthCheckIconProps = {
  isUnhealthy?: boolean;
  errorMessage: string;
  placement: TooltipPlacement;
  title: React.ReactNode;
};
export function HealthCheckIcon({ placement, title }: HealthCheckIconProps) {
  return (
    <Tooltip placement={placement} title={title}>
      <img src="/data.svg" />
      <Text style={{ padding: '0 0.25rem' }}>No Data</Text>
    </Tooltip>
  );
}

export function renderExperimentNameColumn(
  name: string,
  record: SuperExperiment,
) {
  let experimentName = truncate(name?.split('.')[1]);
  if (!experimentName) {
    experimentName = record?.name;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {record.type == ADAPTIVE ? <AdaptiveIcon /> : <ABIcon />}
      <div style={{ paddingLeft: '0.75rem' }}>
        <Tooltip placement="top" title={name}>
          <Text>{experimentName}</Text>
        </Tooltip>
        <div>
          <Text style={{ marginRight: '0.25rem' }} type="secondary">
            {record?.type == ADAPTIVE ? 'Adaptive' : 'A/B Test'}
          </Text>
          {record?.type == ADAPTIVE ? (
            <Tooltip title="Feature is released to a small group for testing, by invitation only.">
              <AdaptiveTrialsCount experimentName={experimentName} />
              <Tag color="#136FD8">Closed Beta</Tag>
            </Tooltip>
          ) : (
            ''
          )}
          {record?.isOverlap && (
            <Tag color="blue" bordered={false}>
              Overlapping
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
}

export function renderStatusColumn(state: string) {
  const capStatus = capitalize(state);
  const { color, content, tooltip } = getStatusTagConfig(capStatus);

  const tag = (
    <Tag color={color} bordered={false}>
      {content}
    </Tag>
  );

  if (tooltip) return <Tooltip title={tooltip}>{tag}</Tooltip>;

  return tag;
}

type StatusTagConfig = {
  color?: string;
  content: string;
  tooltip?: string;
};

export function getStatusTagConfig(state: string): StatusTagConfig {
  const capStatus = capitalize(state);

  switch (capStatus) {
    case 'Scheduled':
      return {
        color: 'purple',
        content: capStatus,
      };
    case 'Requested':
      return {
        content: capStatus,
      };
    case 'Running':
    case 'Started':
      return {
        color: 'blue',
        content: 'Running',
      };
    case 'Completed':
      return {
        color: 'green',
        content: capStatus,
      };
    case 'Aborted':
      return {
        color: 'magenta',
        content: capStatus,
      };
    case 'Cancelled':
      return {
        color: '#cd201f',
        content: capStatus,
        tooltip: 'Failed to obtain approval within the specified days limit',
      };
    default:
      return {
        content: capStatus,
      };
  }
}

export function renderCapitalizedName(name: string) {
  return name
    .split('@')[0]
    .split('.')
    .map((splitName) => capitalize(splitName))
    ?.join(' ');
}

export function renderOwnerColumn(name: string) {
  if (!name) {
    return <SubtractUniversal />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Text>{renderCapitalizedName(name)}</Text>
      <Text type="secondary">{name}</Text>
    </div>
  );
}

export function renderDateColumn(dateType: string, record: SuperExperiment) {
  switch (dateType) {
    case 'createdAt': {
      const createdAt = transformDate(record?.created_at);
      const createdAtDate = formatUTCStringToDateTimeString(createdAt);
      return (
        <>
          <Text>{createdAtDate}</Text>{' '}
          <Text type="secondary">{getTimezoneDisplay()}</Text>
        </>
      );
    }

    case 'start_time': {
      const startDate = transformDate(record?.start_time);
      return (
        <>
          <Text>{startDate}</Text>{' '}
          <Text type="secondary">{getTimezoneDisplay()}</Text>
        </>
      );
    }

    case 'end_time': {
      const endDate = transformDate(record?.end_time);
      return (
        <>
          <Text>{endDate}</Text>{' '}
          <Text type="secondary">{getTimezoneDisplay()}</Text>
        </>
      );
    }

    default:
      return null;
  }
}

export function renderVariablesColumn(variables: string[] | Variable[]) {
  const MAX_VARIABLE_COUNT = 2;
  const count = variables.length;

  let variableNames = [];
  if (typeof variables[0] === 'string') {
    variableNames = variables;
  } else {
    variableNames = variables.map((variable) => variable.name);
  }

  const transformVariableDisplay = (start: number, end: number) => {
    return `${variableNames?.slice(0, start).join(',')}... + ${end}`;
  };

  if (count > MAX_VARIABLE_COUNT) {
    let result = '';
    if (variableNames[0].length >= 26) {
      result = transformVariableDisplay(
        MAX_VARIABLE_COUNT - 1,
        count + 1 - MAX_VARIABLE_COUNT,
      );
    } else {
      result = transformVariableDisplay(
        MAX_VARIABLE_COUNT,
        count - MAX_VARIABLE_COUNT,
      );
    }

    return <Tooltip title={variableNames.join(', ')}>{result}</Tooltip>;
  } else {
    return variableNames?.join(', ');
  }
}

export function renderDurationColumn(status: string, record: SuperExperiment) {
  const startDate = transformDate(record?.start_time);
  const endDate = transformDate(record?.end_time);
  const daysDifference = calculateDaysDifference(startDate, endDate);
  const displayProgress = transformProgress(status, startDate, endDate);
  const completedProgess = extractCompletionProgress(startDate, endDate);

  return (
    <div>
      {displayProgress}
      {completedProgess}/<Text key={daysDifference}>{daysDifference}</Text>
    </div>
  );
}

export function renderCountryColumn(record: SuperExperiment) {
  const countries = record.constraints?.filter((d) => d.target === 'ctry');

  return countries?.length > 0 ? (
    countries.map((country) => country.value)
  ) : (
    <SubtractUniversal />
  );
}

export function renderCityColumn(record: SuperExperiment) {
  const cities = record.constraints?.filter((d) => d.target === 'city');
  const citiesCache = Service.cache.getAllCities();
  const cityNames = [];

  cities?.forEach((city) => {
    citiesCache.forEach((cityObj) => {
      if (cityObj.id === parseInt(city.value, 10)) {
        cityNames.push(cityObj.name);
      }
    });
  });

  return cities?.length > 0 ? cityNames.join(',') : <SubtractUniversal />;
}

export function renderProductUser(userType: string, metadata) {
  switch (userType) {
    case 'productAnalyst':
      return metadata?.productAnalyst ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{renderCapitalizedName(metadata.productAnalyst)}</Text>
          <Text type="secondary">{metadata.productAnalyst}</Text>
        </div>
      ) : (
        <SubtractUniversal />
      );
    case 'productManager':
      return metadata?.productManager ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{renderCapitalizedName(metadata.productManager)}</Text>
          <Text type="secondary">{metadata.productManager}</Text>
        </div>
      ) : (
        <SubtractUniversal />
      );
    default:
      return <SubtractUniversal />;
  }
}

export function groupBy(list, keyGetter) {
  const map = new Map();
  list?.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}
